import { useEffect, useCallback } from "react";
import clone from "clone";
import { useQuery, useMutation } from "@apollo/client";
import Cookies from "js-cookie";
import gql from "graphql-tag";
import base64 from "base-64";
import { Store } from "./store";
import useNotification from "./hooks/useNotification";
import { apolloClient } from "./apolloClient";
import ReactGA4 from "react-ga4";
import { USER } from "./types";
import { CHECKOUT } from "./checkoutContext";

export default props => {
  const { checkout } = Store.useState(s => s);

  const { addNotification } = useNotification();

  const { data: sessionData } = useQuery(GET_SESSION);
  const [login] = useMutation(POST_LOGIN);
  const [logout] = useMutation(POST_LOGOUT);
  const [register] = useMutation(POST_REGISTER);
  const [verify] = useMutation(POST_VERIFY);

  const resetStore = useCallback(async () => {
    await apolloClient.queryManager.fetchQueryRejectFns;
    await apolloClient.cache.reset();
  }, [apolloClient]);

  useEffect(() => {
    if (sessionData) {
      Store.update(s => {
        s.session = clone(sessionData.session);
      });
      if (sessionData.session?.jwt) {
        Cookies.set("jwt", sessionData.session.jwt, { expires: 180 });
        ReactGA4.set({ userId: sessionData.session.user._id });
      } else Cookies.remove("jwt");
    }
  }, [sessionData]);

  const loginHook = useCallback(async ({ password, email }) => {
    const { data } = await login({ variables: { email: base64.encode(email), password: base64.encode(password), checkoutId: checkout?.id } });
    const session = data.login.session;
    Cookies.set("jwt", session.jwt, { expires: 180 });
    ReactGA4.set({ userId: session.user._id });
    Store.update(s => {
      s.session = clone(session);
    });
    resetStore();
    return session;
  }, []);

  const verifyHook = useCallback(async verificationData => {
    const { data } = await verify({ variables: verificationData });
    const session = data.userVerify.session;
    Cookies.set("jwt", session.jwt, { expires: 180 });
    Store.update(s => {
      s.session = clone(session);
    });
    addNotification({ ok: 1, message: data.message });
    return data.userVerify;
  }, []);

  const logoutHook = useCallback(async () => {
    try {
      await logout({ variables: { checkoutId: checkout?.id } });
      Cookies.remove("jwt");
      resetStore();
    } catch (error) {
      addNotification({ ok: 0, message: error.toString() });
    }
  }, []);

  const registerHook = useCallback(async ({ email, password, firstName, lastName, withVerificationCode }) => {
    const { data } = await register({
      variables: {
        email: base64.encode(email),
        password: base64.encode(password),
        withVerificationCode,
        firstName,
        lastName
      }
    });
    return { message: data.register.message };
  }, []);

  useEffect(() => {
    Store.update(s => {
      s.login = loginHook;
      s.logout = logoutHook;
      s.register = registerHook;
      s.verify = verifyHook;
      s.resetStore = resetStore;
    });
  }, []);

  return props.children;
};

const SESSION_FIELDS = `
  user {
    ${USER}
  }
  jwt
  expires
`;

export const GET_SESSION = gql(`
  query {
    session {
      ${SESSION_FIELDS}
    }
}`);

export const POST_LOGIN = gql(`
  mutation($password: String!, $email: String!, $checkoutId: String) {
    login(password: $password, email: $email, checkoutId: $checkoutId) {
      session {
        ${SESSION_FIELDS}
      }
      sessionsCount
      checkout {
        ${CHECKOUT}
      }
    }
}`);

export const POST_LOGOUT = gql(`
  mutation($checkoutId: String) {
    logout(checkoutId: $checkoutId) {
      ok
      checkout {
        ${CHECKOUT}
      }
    }
  }
`);

export const POST_VERIFY = gql(`
  mutation($token: String, $code: Int, $checkoutId: String) {
    userVerify(token: $token, code: $code, checkoutId: $checkoutId) {
      session {
        ${SESSION_FIELDS}
      }
      message
      checkout {
        ${CHECKOUT}
      }
    }
  }
`);

export const POST_REGISTER = gql(`
  mutation($password: String!, $email: String!, $firstName: String!, $lastName: String!, $withVerificationCode: Boolean) {
    register(password: $password, email: $email, firstName: $firstName, lastName: $lastName, withVerificationCode: $withVerificationCode) {
      message
    }
}`);

export const POST_LIST_OPTIN = gql(`
  mutation($listRef: ID!, $email: String!) {
    listOptin(listRef: $listRef, email: $email)
  }
`);

export const POST_LIST_OPTOUT = gql(`
  mutation($listRef: ID!, $email: String!, $reason: String!) {
    listOptout(listRef: $listRef, email: $email, reason: $reason)
  }
`);

export const POST_PASSWORD_USER_SESSION = gql(`
  mutation($token: String!) {
    userVerifyPasswordToken(token: $token) {
      ${USER}
    }
}`);

export const POST_USER_CREATE_PASSWORD_TOKEN = gql(`
  mutation($email: String!, $withVerificationCode: Boolean) {
    userCreatePasswordToken(email: $email, withVerificationCode: $withVerificationCode)
}`);

export const POST_USER_PWD_RESET = gql(`
  mutation($password: String!, $token: String!) {
    userPasswordReset(password: $password, token: $token)
}`);

export const POST_USER_UPDATE = gql(`
  mutation($email: String, $password: String, $firstName: String, $lastName: String, $telephone: String, $organisation: String, $taxNumber: String, $addresses: [AddressInput], $preferences: JSON) {
    user: userUpdate(email: $email, password: $password, firstName: $firstName, lastName: $lastName, telephone: $telephone, organisation: $organisation, taxNumber: $taxNumber, addresses: $addresses, preferences: $preferences) {
      ${USER}
    }
}`);

export const GET_USER = gql(`
  query {
    user {
      ${USER}
    }
  }`);

export const POST_BUYER_DEACTIVATE = gql(`
  mutation {
    buyerDeactivate
  }
`);
